export type BondRankName =
  | 'Stranger'
  | 'Acquaintance'
  | 'Contact'
  | 'Friend'
  | 'Buddy'
  | 'Ally'
  | 'Confidant'
  | 'Partner'
  | 'Companion'
  | 'Soulmate'
  | 'Eternal';

export interface BondRank {
  level: number;
  name: BondRankName;
  xpRequired: number;
  description: string;
  color: string;
  icon: string;
}

export const BOND_RANKS: BondRank[] = [
  {
    level: 0,
    name: 'Stranger',
    xpRequired: 0,
    description: 'Someone you\'ve just met or barely know',
    color: '#6B7280',
    icon: 'User'
  },
  {
    level: 1,
    name: 'Acquaintance',
    xpRequired: 100,
    description: 'Someone you recognize and have had brief interactions with',
    color: '#9CA3AF',
    icon: 'UserPlus'
  },
  {
    level: 2,
    name: 'Contact',
    xpRequired: 300,
    description: 'Someone you interact with occasionally and can reach out to',
    color: '#60A5FA',
    icon: 'Contact'
  },
  {
    level: 3,
    name: 'Friend',
    xpRequired: 600,
    description: 'Someone you enjoy spending time with and share common interests',
    color: '#34D399',
    icon: 'Smile'
  },
  {
    level: 4,
    name: 'Buddy',
    xpRequired: 1000,
    description: 'A reliable friend you can count on for support and fun',
    color: '#FBBF24',
    icon: 'Users'
  },
  {
    level: 5,
    name: 'Ally',
    xpRequired: 1500,
    description: 'Someone who has your back and stands with you',
    color: '#F97316',
    icon: 'Shield'
  },
  {
    level: 6,
    name: 'Confidant',
    xpRequired: 2200,
    description: 'Someone you trust with your deeper thoughts and feelings',
    color: '#EC4899',
    icon: 'Heart'
  },
  {
    level: 7,
    name: 'Partner',
    xpRequired: 3000,
    description: 'A deep bond built on mutual trust and shared experiences',
    color: '#8B5CF6',
    icon: 'Handshake'
  },
  {
    level: 8,
    name: 'Companion',
    xpRequired: 4000,
    description: 'Someone who walks alongside you through life\'s journey',
    color: '#06B6D4',
    icon: 'HeartHandshake'
  },
  {
    level: 9,
    name: 'Soulmate',
    xpRequired: 5500,
    description: 'A rare connection that transcends ordinary friendship',
    color: '#A855F7',
    icon: 'Sparkles'
  },
  {
    level: 10,
    name: 'Eternal',
    xpRequired: 7500,
    description: 'An unbreakable bond that defines your life',
    color: '#EAB308',
    icon: 'Crown'
  }
];

export function getBondRankFromLevel(level: number): BondRank {
  const clampedLevel = Math.max(0, Math.min(10, level));
  return BOND_RANKS[clampedLevel];
}

export function getBondRankFromXP(xp: number): BondRank {
  for (let i = BOND_RANKS.length - 1; i >= 0; i--) {
    if (xp >= BOND_RANKS[i].xpRequired) {
      return BOND_RANKS[i];
    }
  }
  return BOND_RANKS[0];
}

export function getXPToNextRank(currentXP: number): { nextRank: BondRank | null; xpNeeded: number } {
  const currentRank = getBondRankFromXP(currentXP);
  const nextRankIndex = currentRank.level + 1;

  if (nextRankIndex >= BOND_RANKS.length) {
    return { nextRank: null, xpNeeded: 0 };
  }

  const nextRank = BOND_RANKS[nextRankIndex];
  return {
    nextRank,
    xpNeeded: nextRank.xpRequired - currentXP
  };
}

export function getXPProgressInRank(currentXP: number): { progress: number; currentRankXP: number; nextRankXP: number } {
  const currentRank = getBondRankFromXP(currentXP);
  const nextRankIndex = currentRank.level + 1;

  if (nextRankIndex >= BOND_RANKS.length) {
    return { progress: 100, currentRankXP: currentRank.xpRequired, nextRankXP: currentRank.xpRequired };
  }

  const nextRank = BOND_RANKS[nextRankIndex];
  const xpInCurrentRank = currentXP - currentRank.xpRequired;
  const xpNeededForNextRank = nextRank.xpRequired - currentRank.xpRequired;
  const progress = Math.round((xpInCurrentRank / xpNeededForNextRank) * 100);

  return {
    progress: Math.min(100, Math.max(0, progress)),
    currentRankXP: currentRank.xpRequired,
    nextRankXP: nextRank.xpRequired
  };
}
